// src/pages/Rewards.jsx — Codes récompense
// Flux : Rewards → adminPaiement → adminHttp → Worker Admin → Worker Paiement
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Gift,
  Plus,
  RefreshCw,
  Search,
  Pencil,
  Power,
  AlertTriangle,
  CheckCircle2,
  Upload,
  X,
} from 'lucide-react';
import { PageHeader, StatusBadge, EmptyState } from '../components/ui/UIComponents';
import {
  listerCodes,
  creerCode,
  modifierCode,
  setCodeActif,
  migrerCodesDepuisSheets,
} from '../services/adminPaiement';
import { useI18n } from '../i18n/useI18n';

function display(v) {
  if (v === undefined || v === null || v === '') return '—';
  return v;
}

function formatUtilisations(actuelles, max) {
  const a = Number(actuelles);
  const m = Number(max);
  const left = Number.isFinite(a) ? a : 0;
  if (!Number.isFinite(m) || m === 0) return `${left} / Illimité`;
  return `${left} / ${m}`;
}

function formatExpiration(dateExp, heureExp) {
  if (!dateExp && !heureExp) return 'Aucune expiration';
  if (dateExp && heureExp) return `${dateExp} ${heureExp}`;
  return dateExp || heureExp || 'Aucune expiration';
}

const ERROR_MESSAGES = {
  code_manquant: 'Le code est obligatoire.',
  montantDsi_invalide: 'Le montant DSI est invalide (entier strictement positif).',
  utilisationsMax_invalide: 'Le nombre maximum d’utilisations est invalide.',
  code_deja_existant: 'Ce code existe déjà.',
  code_introuvable: 'Code introuvable.',
  aucun_champ_modifiable: 'Aucune modification n’a été renseignée.',
  utilisations_actuelles_non_modifiable: 'Le compteur d’utilisations ne peut pas être modifié.',
  max_inferieur_aux_usages:
    'La nouvelle limite est inférieure au nombre d’utilisations déjà effectuées.',
};

function userErrorMessage(err) {
  const raw =
    (typeof err?.body?.erreur === 'string' && err.body.erreur) ||
    (typeof err?.body?.error === 'string' && err.body.error) ||
    err?.message ||
    '';
  const key = raw.toString().trim();
  if (ERROR_MESSAGES[key]) return ERROR_MESSAGES[key];
  // parfois "400 code_deja_existant"
  for (const k of Object.keys(ERROR_MESSAGES)) {
    if (key.includes(k)) return ERROR_MESSAGES[k];
  }
  if (err?.status === 403) return 'Vous n’avez pas les droits nécessaires pour cette action.';
  if (err?.status === 401) return 'Session expirée. Veuillez vous reconnecter.';
  return key || 'Une erreur est survenue.';
}

const emptyForm = () => ({
  code: '',
  montantDsi: '',
  utilisationsMax: '0',
  dateExpiration: '',
  heureExpiration: '',
  actif: true,
});

export default function Rewards() {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [codes, setCodes] = useState([]);
  const [total, setTotal] = useState(0);
  const [tronque, setTronque] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterActif, setFilterActif] = useState('all'); // all | active | inactive
  const [toast, setToast] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create'); // create | edit
  const [form, setForm] = useState(emptyForm());
  const [formError, setFormError] = useState(null);
  const [editOriginal, setEditOriginal] = useState(null);

  const [confirmActif, setConfirmActif] = useState(null); // { code, actif }
  const [confirmMigrate, setConfirmMigrate] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);

  const showToast = (type, message) => setToast({ type, message });

  const loadCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listerCodes(200);
      const list = Array.isArray(res?.codes) ? res.codes : [];
      setCodes(list);
      setTotal(typeof res?.total === 'number' ? res.total : list.length);
      setTronque(Boolean(res?.tronque));
    } catch (err) {
      setCodes([]);
      setTotal(0);
      setTronque(false);
      setError(userErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  const filtered = useMemo(() => {
    return codes.filter((c) => {
      if (filterActif === 'active' && !c.actif) return false;
      if (filterActif === 'inactive' && c.actif) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(c.code || '').toString().toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [codes, search, filterActif]);

  const openCreate = () => {
    setFormMode('create');
    setForm(emptyForm());
    setEditOriginal(null);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setFormMode('edit');
    setEditOriginal(row);
    setForm({
      code: row.code || '',
      montantDsi: row.montantDsi != null ? String(row.montantDsi) : '',
      utilisationsMax: row.utilisationsMax != null ? String(row.utilisationsMax) : '0',
      dateExpiration: row.dateExpiration || '',
      heureExpiration: row.heureExpiration || '',
      actif: Boolean(row.actif),
    });
    setFormError(null);
    setFormOpen(true);
  };

  const submitForm = async () => {
    setFormError(null);
    const code = (form.code || '').toString().trim().toUpperCase();
    if (!code) {
      setFormError('Le code est obligatoire.');
      return;
    }
    const montant = Number(form.montantDsi);
    if (!Number.isInteger(montant) || montant <= 0) {
      setFormError('Le montant DSI doit être un entier strictement positif.');
      return;
    }
    const maxU = form.utilisationsMax === '' ? 0 : Number(form.utilisationsMax);
    if (!Number.isInteger(maxU) || maxU < 0) {
      setFormError('Le nombre maximum d’utilisations doit être un entier ≥ 0 (0 = illimité).');
      return;
    }

    setActionLoading(true);
    try {
      if (formMode === 'create') {
        const payload = {
          code,
          montantDsi: montant,
          utilisationsMax: maxU,
          actif: Boolean(form.actif),
        };
        if (form.dateExpiration) payload.dateExpiration = form.dateExpiration;
        if (form.heureExpiration) payload.heureExpiration = form.heureExpiration;
        await creerCode(payload);
        showToast('ok', `Code ${code} créé.`);
      } else {
        // partial body — only changed fields
        const payload = { code };
        let changed = false;
        const orig = editOriginal || {};
        if (Number(orig.montantDsi) !== montant) {
          payload.montantDsi = montant;
          changed = true;
        }
        if (Number(orig.utilisationsMax) !== maxU) {
          payload.utilisationsMax = maxU;
          changed = true;
        }
        const d = form.dateExpiration || '';
        const h = form.heureExpiration || '';
        if ((orig.dateExpiration || '') !== d) {
          payload.dateExpiration = d || null;
          changed = true;
        }
        if ((orig.heureExpiration || '') !== h) {
          payload.heureExpiration = h || null;
          changed = true;
        }
        if (Boolean(orig.actif) !== Boolean(form.actif)) {
          payload.actif = Boolean(form.actif);
          changed = true;
        }
        if (!changed) {
          setFormError('Aucune modification n’a été renseignée.');
          setActionLoading(false);
          return;
        }
        await modifierCode(payload);
        showToast('ok', `Code ${code} mis à jour.`);
      }
      setFormOpen(false);
      await loadCodes();
    } catch (err) {
      setFormError(userErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const runSetActif = async () => {
    if (!confirmActif) return;
    setActionLoading(true);
    try {
      await setCodeActif(confirmActif.code, confirmActif.actif);
      showToast('ok', confirmActif.actif ? `Code ${confirmActif.code} activé.` : `Code ${confirmActif.code} désactivé.`);
      setConfirmActif(null);
      await loadCodes();
    } catch (err) {
      showToast('err', userErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const runMigrate = async () => {
    setConfirmMigrate(false);
    setActionLoading(true);
    setMigrateResult(null);
    try {
      const res = await migrerCodesDepuisSheets();
      setMigrateResult(res);
      showToast('ok', 'Import initial terminé.');
      await loadCodes();
    } catch (err) {
      showToast('err', userErrorMessage(err));
    } finally {
      setActionLoading(false);
    }
  };

  const toastClass =
    toast?.type === 'err'
      ? 'bg-rose-50 border-rose-200 text-rose-800'
      : 'bg-emerald-50 border-emerald-200 text-emerald-800';

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title="Récompenses"
        subtitle="Gestion des codes récompense DSI."
        actions={
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={loadCodes} className="dsi-btn-secondary text-xs" disabled={loading}>
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> {t('common.refresh')}
            </button>
            <button type="button" onClick={openCreate} className="dsi-btn-primary text-xs">
              <Plus size={14} /> Créer un code
            </button>
          </div>
        }
      />

      {toast && (
        <div className={`p-3 rounded-2xl border text-xs font-bold flex justify-between ${toastClass}`}>
          <span className="flex items-center gap-2">
            {toast.type === 'err' ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
            {toast.message}
          </span>
          <button type="button" onClick={() => setToast(null)} className="underline">
            {t('common.close')}
          </button>
        </div>
      )}

      {tronque && (
        <div className="p-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-xs">
          La liste reçue est partielle ({codes.length} code(s) affiché(s)
          {typeof total === 'number' ? ` sur un total indiqué de ${total}` : ''}). Affinez ou rechargez si besoin.
        </div>
      )}

      <div className="dsi-card p-4 flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            className="dsi-input pl-9"
            placeholder="Rechercher un code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select className="dsi-input w-auto" value={filterActif} onChange={(e) => setFilterActif(e.target.value)}>
          <option value="all">Tous</option>
          <option value="active">Actifs</option>
          <option value="inactive">Inactifs</option>
        </select>
      </div>

      {loading ? (
        <div className="dsi-card p-12 text-center text-xs text-slate-500">{t('common.loading')}</div>
      ) : error ? (
        <div className="dsi-card p-8 text-center space-y-3">
          <p className="text-sm text-rose-700 font-semibold">{error}</p>
          <button type="button" className="dsi-btn-secondary text-xs" onClick={loadCodes}>
            {t('common.retry')}
          </button>
        </div>
      ) : (
        <div className="dsi-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3">Code</th>
                  <th className="px-4 py-3">Récompense</th>
                  <th className="px-4 py-3">Utilisations</th>
                  <th className="px-4 py-3">Expiration</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((row) => (
                  <tr key={row.code} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs font-semibold">{display(row.code)}</td>
                    <td className="px-4 py-3 font-bold">
                      {row.montantDsi != null ? `${Number(row.montantDsi).toLocaleString('fr-FR')} DSI` : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {formatUtilisations(row.utilisationsActuelles, row.utilisationsMax)}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {formatExpiration(row.dateExpiration, row.heureExpiration)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        statusKey={row.actif ? 'ACTIF' : 'INACTIF'}
                        text={row.actif ? 'Actif' : 'Inactif'}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex flex-wrap gap-1 justify-end">
                        <button type="button" className="dsi-btn-ghost text-xs" onClick={() => openEdit(row)}>
                          <Pencil size={14} /> Modifier
                        </button>
                        <button
                          type="button"
                          className="dsi-btn-ghost text-xs"
                          onClick={() =>
                            setConfirmActif({
                              code: row.code,
                              actif: !row.actif,
                            })
                          }
                        >
                          <Power size={14} /> {row.actif ? 'Désactiver' : 'Activer'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!filtered.length && (
            <EmptyState
              title="Aucun code récompense"
              message={
                codes.length
                  ? 'Aucun code ne correspond à votre recherche.'
                  : 'Créez le premier code pour commencer.'
              }
            />
          )}
        </div>
      )}

      {/* Zone secondaire — migration initiale */}
      <div className="dsi-card p-5 space-y-3 border border-slate-200">
        <div className="flex items-center gap-2">
          <Upload size={16} className="text-slate-500" />
          <h3 className="text-sm font-bold text-slate-900">Import initial des codes</h3>
        </div>
        <p className="text-xs text-slate-500">
          Récupère les anciens codes depuis Sheets pour un premier chargement. Ce n’est pas une synchronisation
          continue.
        </p>
        <button
          type="button"
          className="dsi-btn-secondary text-xs"
          disabled={actionLoading}
          onClick={() => setConfirmMigrate(true)}
        >
          <Upload size={14} /> Lancer l’import
        </button>
        {migrateResult && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs space-y-1">
            <p className="font-semibold text-slate-800">Résultat de l’import</p>
            <p>Lignes analysées : {display(migrateResult.analyses)}</p>
            <p>Codes importés : {display(migrateResult.importes)}</p>
            <p>Déjà présents : {display(migrateResult.ignoresExistants)}</p>
            <p>Lignes invalides : {display(migrateResult.ignoresInvalides)}</p>
            {Array.isArray(migrateResult.details) && migrateResult.details.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-slate-600">Détails</summary>
                <ul className="mt-1 max-h-40 overflow-y-auto space-y-0.5 text-slate-500">
                  {migrateResult.details.map((d, i) => (
                    <li key={i} className="font-mono">
                      {typeof d === 'string' ? d : JSON.stringify(d)}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      {/* Modale création / édition */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => !actionLoading && setFormOpen(false)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center">
              <h3 className="font-bold flex items-center gap-2">
                <Gift size={16} />
                {formMode === 'create' ? 'Créer un code' : `Modifier ${form.code}`}
              </h3>
              <button type="button" className="p-2 hover:bg-slate-100 rounded-xl" onClick={() => setFormOpen(false)} disabled={actionLoading}>
                <X size={18} />
              </button>
            </div>
            {formError && (
              <div className="p-2 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs">{formError}</div>
            )}
            <div className="space-y-3 text-xs">
              <label className="block space-y-1">
                <span className="font-semibold text-slate-600">Code</span>
                <input
                  className="dsi-input font-mono uppercase"
                  value={form.code}
                  disabled={formMode === 'edit' || actionLoading}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="BIENVENUE2026"
                />
              </label>
              <label className="block space-y-1">
                <span className="font-semibold text-slate-600">Montant DSI</span>
                <input
                  className="dsi-input"
                  type="number"
                  min={1}
                  step={1}
                  value={form.montantDsi}
                  disabled={actionLoading}
                  onChange={(e) => setForm({ ...form, montantDsi: e.target.value })}
                />
              </label>
              <label className="block space-y-1">
                <span className="font-semibold text-slate-600">Utilisations max (0 = illimité)</span>
                <input
                  className="dsi-input"
                  type="number"
                  min={0}
                  step={1}
                  value={form.utilisationsMax}
                  disabled={actionLoading}
                  onChange={(e) => setForm({ ...form, utilisationsMax: e.target.value })}
                />
              </label>
              {formMode === 'edit' && editOriginal && (
                <p className="text-[11px] text-slate-500">
                  Utilisations actuelles :{' '}
                  <strong>{display(editOriginal.utilisationsActuelles)}</strong> (lecture seule)
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                <label className="block space-y-1">
                  <span className="font-semibold text-slate-600">Date d’expiration</span>
                  <input
                    className="dsi-input"
                    type="date"
                    value={form.dateExpiration}
                    disabled={actionLoading}
                    onChange={(e) => setForm({ ...form, dateExpiration: e.target.value })}
                  />
                </label>
                <label className="block space-y-1">
                  <span className="font-semibold text-slate-600">Heure</span>
                  <input
                    className="dsi-input"
                    type="time"
                    step={1}
                    value={form.heureExpiration}
                    disabled={actionLoading}
                    onChange={(e) => setForm({ ...form, heureExpiration: e.target.value })}
                  />
                </label>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Boolean(form.actif)}
                  disabled={actionLoading}
                  onChange={(e) => setForm({ ...form, actif: e.target.checked })}
                />
                <span className="font-semibold text-slate-600">Code actif</span>
              </label>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" onClick={() => setFormOpen(false)} disabled={actionLoading}>
                {t('common.cancel')}
              </button>
              <button type="button" className="dsi-btn-primary text-xs" onClick={submitForm} disabled={actionLoading}>
                {actionLoading ? '…' : formMode === 'create' ? 'Créer' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmActif && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => !actionLoading && setConfirmActif(null)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4">
            <h3 className="font-bold">
              {confirmActif.actif ? 'Activer ce code ?' : 'Désactiver ce code ?'}
            </h3>
            <p className="text-xs text-slate-600">
              Code <span className="font-mono font-semibold">{confirmActif.code}</span>
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" onClick={() => setConfirmActif(null)} disabled={actionLoading}>
                {t('common.cancel')}
              </button>
              <button type="button" className="dsi-btn-primary text-xs" onClick={runSetActif} disabled={actionLoading}>
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmMigrate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setConfirmMigrate(false)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4">
            <h3 className="font-bold">Lancer l’import initial ?</h3>
            <p className="text-xs text-slate-600">
              Les codes déjà présents seront ignorés. Les lignes invalides seront signalées dans le résumé.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" onClick={() => setConfirmMigrate(false)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="dsi-btn-primary text-xs" onClick={runMigrate}>
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
